import React, { useState } from 'react';
import { Form, Row, Col, Flex, message, Spin } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  CustomInput,
  CustomSelect,
  CustomDatePicker,
  CustomRadioGroup,
  CustomButton,
  CustomShipmentText,
} from '@/components';
import { COUNTRY_OPTIONS } from '@/common/static_data';
import {
  updateDeliveryAddressTrackingLink,
  UpdateDeliveryAddressTrackingLinkModel,
} from '@/apis/shipment';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface UpdateAddressFormValues {
  new_address_line_1: string;
  new_address_line_2?: string;
  new_city: string;
  new_state: string;
  new_postal_code: string;
  new_country: string;
  change_scope: 'PERMANENT' | 'TEMPORARY';
  temporary_start_date?: dayjs.Dayjs;
  temporary_end_date?: dayjs.Dayjs;
}

interface UpdateDeliveryAddressProps {
  address?: string;
  onCancel?: () => void;
  externalToken?: string;
  shipmentTimezone?: string;
  refetchSharePageData?: () => void;
  handleCardActions?: (
    showCard: boolean,
    isOpen: boolean,
    name: string
  ) => void;
  askAddressChangeScope?: boolean;
}

const normalizeWhitespace = (str: string | undefined | null): string => {
  if (!str) return '';
  return str.trim().replace(/\s+/g, ' ');
};

const cleanAddress = (str: string | undefined | null): string => {
  if (!str) return '';
  return normalizeWhitespace(str)
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const isStateRequired = (country: string | undefined | null): boolean => {
  const code = (country || '').toUpperCase();
  return ['US', 'USA', 'CA', 'CAN', 'MX', 'MEX', 'AU', 'AUS'].includes(code);
};

const isPostalCodeRequired = (country: string | undefined | null): boolean => {
  const code = (country || '').toUpperCase();
  const noPostalCodeCountries = ['AE', 'QA', 'OM', 'YE', 'BH'];
  return !noPostalCodeCountries.includes(code);
};

const UpdateDeliveryAddress: React.FC<UpdateDeliveryAddressProps> = ({
  address,
  onCancel,
  externalToken,
  shipmentTimezone,
  handleCardActions,
  refetchSharePageData,
  askAddressChangeScope = true,
}) => {
  const [form] = Form.useForm<UpdateAddressFormValues>();
  const changeScope = Form.useWatch('change_scope', form);
  const selectedCountry = Form.useWatch('new_country', form);
  const [submitting, setSubmitting] = useState(false);

  const disabledStartDate = (current?: dayjs.Dayjs): boolean => {
    if (!current) return false;
    const tz = shipmentTimezone || dayjs.tz.guess();
    const todayInShipmentTz = dayjs().tz(tz).startOf('day');
    const currentDateInShipmentTz = dayjs(current.format('YYYY-MM-DD'))
      .tz(tz, true)
      .startOf('day');
    return currentDateInShipmentTz.isBefore(todayInShipmentTz);
  };

  const disabledEndDate = (current?: dayjs.Dayjs): boolean => {
    if (!current) return false;
    const tz = shipmentTimezone || dayjs.tz.guess();
    const todayInShipmentTz = dayjs().tz(tz).startOf('day');
    const currentDateInShipmentTz = dayjs(current.format('YYYY-MM-DD'))
      .tz(tz, true)
      .startOf('day');

    if (currentDateInShipmentTz.isBefore(todayInShipmentTz)) {
      return true;
    }

    const startDate = form.getFieldValue('temporary_start_date');
    if (startDate) {
      const startDateInShipmentTz = dayjs(startDate.format('YYYY-MM-DD'))
        .tz(tz, true)
        .startOf('day');
      return currentDateInShipmentTz.isBefore(startDateInShipmentTz);
    }
    return false;
  };

  const handleFinish = (values: UpdateAddressFormValues) => {
    if (!externalToken) {
      message.error('Token is missing. Cannot submit address change.');
      return;
    }

    const normLine1 = normalizeWhitespace(values.new_address_line_1);
    const normLine2 = values.new_address_line_2
      ? normalizeWhitespace(values.new_address_line_2)
      : '';
    const normCity = normalizeWhitespace(values.new_city);
    const normState = normalizeWhitespace(values.new_state);
    const normPostal = normalizeWhitespace(values.new_postal_code);
    const normCountry = normalizeWhitespace(values.new_country);

    const effectiveChangeScope = askAddressChangeScope
      ? values.change_scope
      : 'PERMANENT';

    const newAddressParts = [
      normLine1,
      normLine2,
      normCity,
      normState,
      normPostal,
      normCountry,
    ];
    const newAddressStr = newAddressParts.filter(Boolean).join(', ');
    const currentAddressStr = address || '';
    if (
      cleanAddress(currentAddressStr) === cleanAddress(newAddressStr) &&
      effectiveChangeScope === 'PERMANENT'
    ) {
      message.error('New address cannot be the same as the current address.');
      return;
    }

    setSubmitting(true);

    const payload: UpdateDeliveryAddressTrackingLinkModel = {
      token: externalToken,
      issue_type: 'ADDRESS_UPDATE',
      new_address_line_1: normLine1,
      new_address_line_2: normLine2 || undefined,
      new_city: normCity,
      new_state: normState,
      new_postal_code: normPostal,
      new_country: normCountry,
      change_scope: effectiveChangeScope,
      temporary_start_date:
        effectiveChangeScope === 'TEMPORARY' && values.temporary_start_date
          ? values.temporary_start_date.format('YYYY-MM-DD')
          : undefined,
      temporary_end_date:
        effectiveChangeScope === 'TEMPORARY' && values.temporary_end_date
          ? values.temporary_end_date.format('YYYY-MM-DD')
          : undefined,
    };

    updateDeliveryAddressTrackingLink(payload)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          message.success(data.message);
          refetchSharePageData?.();
          handleCardActions?.(false, false, '');
          form.resetFields();
        } else {
          message.error(
            data?.message || 'Failed to submit delivery address update request.'
          );
        }
      })
      .catch((err) => {
        const responseBody = err?.response?.data || err?.data;
        const errMsg =
          responseBody?.data?.message ||
          responseBody?.message ||
          'Failed to submit delivery address update request.';
        message.error(errMsg);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Flex vertical gap={24}>
      <Flex vertical gap={8}>
        <CustomShipmentText
          text='Current address'
          fontSize='16px'
          fontWeight={600}
          lineHeight='20px'
          color='var(--text-primary)'
        />
        <CustomShipmentText
          text={address || '--'}
          fontSize='14px'
          fontWeight={400}
          lineHeight='20px'
        />
      </Flex>
      <CustomShipmentText
        text='New address'
        fontSize='16px'
        fontWeight={600}
        lineHeight='20px'
      />
      <Form
        form={form}
        layout='vertical'
        onValuesChange={(changedValues) => {
          if (changedValues.change_scope === 'PERMANENT') {
            form.setFieldsValue({
              temporary_start_date: undefined,
              temporary_end_date: undefined,
            });
          }
        }}
        onFinish={handleFinish}
        initialValues={{ change_scope: 'PERMANENT', new_country: 'US' }}
      >
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Form.Item
              label='Address line 1'
              name='new_address_line_1'
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'Address line 1 is required.',
                },
                {
                  min: 3,
                  message: 'Address line 1 must be at least 3 characters.',
                },
                {
                  max: 150,
                  message: 'Address line 1 cannot exceed 150 characters.',
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <CustomInput placeholder='Enter address line 1' />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label='Address line 2'
              name='new_address_line_2'
              rules={[
                {
                  max: 150,
                  message: 'Address line 2 cannot exceed 150 characters.',
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <CustomInput placeholder='Optional' />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Row gutter={[24, 0]}>
              <Col span={12}>
                <Form.Item
                  label='City'
                  name='new_city'
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: 'City is required.',
                    },
                    {
                      min: 2,
                      message: 'City must be at least 2 characters.',
                    },
                    {
                      max: 100,
                      message: 'City cannot exceed 100 characters.',
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <CustomInput placeholder='Enter City' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='State/Province'
                  name='new_state'
                  dependencies={['new_country']}
                  rules={[
                    {
                      required: isStateRequired(selectedCountry),
                      whitespace: true,
                      message: 'State is required.',
                    },
                    {
                      min: 2,
                      message: 'State must be at least 2 characters.',
                    },
                    {
                      max: 100,
                      message: 'State cannot exceed 100 characters.',
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <CustomInput placeholder='Enter State/Province' />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Row gutter={[24, 0]}>
              <Col span={12}>
                <Form.Item
                  label='Postal Code'
                  name='new_postal_code'
                  dependencies={['new_country']}
                  rules={[
                    {
                      required: isPostalCodeRequired(selectedCountry),
                      whitespace: true,
                      message: 'Postal code is required.',
                    },
                    {
                      min: 3,
                      message: 'Postal code must be at least 3 characters.',
                    },
                    {
                      max: 20,
                      message: 'Postal code cannot exceed 20 characters.',
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <CustomInput placeholder='Enter Postal Code' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Country'
                  name='new_country'
                  rules={[{ required: true, message: 'Country is required.' }]}
                  style={{ marginBottom: 0 }}
                >
                  <CustomSelect
                    placeholder='Select Country'
                    width='100%'
                    height='36px'
                    options={COUNTRY_OPTIONS}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          {askAddressChangeScope && (
            <Col span={24}>
              <Form.Item
                name='change_scope'
                rules={[
                  { required: true, message: 'Change scope is required.' },
                ]}
                style={{ marginBottom: 0 }}
              >
                <CustomRadioGroup
                  gap='24px'
                  options={[
                    { value: 'PERMANENT', label: 'Permanent Address Change' },
                    { value: 'TEMPORARY', label: 'Temporary Address Change' },
                  ]}
                />
              </Form.Item>
            </Col>
          )}

          {askAddressChangeScope && changeScope === 'TEMPORARY' && (
            <Col span={24}>
              <Row gutter={[24, 0]}>
                <Col span={12}>
                  <Form.Item
                    label='Start Date'
                    name='temporary_start_date'
                    rules={[
                      { required: true, message: 'Start date is required.' },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <CustomDatePicker
                      style={{
                        color: 'black',
                        border: 0,
                        boxShadow: 'none',
                      }}
                      placeholder='Select start date'
                      disabledDate={disabledStartDate}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label='End Date'
                    name='temporary_end_date'
                    dependencies={['temporary_start_date']}
                    rules={[
                      { required: true, message: 'End date is required.' },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <CustomDatePicker
                      style={{
                        color: 'black',
                        border: 0,
                        boxShadow: 'none',
                      }}
                      placeholder='Select end date'
                      disabledDate={disabledEndDate}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          )}

          <Col span={24}>
            <Row gutter={[0, 8]} style={{ marginTop: '24px' }}>
              <CustomButton
                text='Submit'
                width='100%'
                className='custom-save-btn'
                htmlType='submit'
                disabled={submitting}
                loader={submitting ? <Spin size='small' /> : null}
              />
              <CustomButton
                width='100%'
                onClick={onCancel}
                text='Cancel'
                className='custom-cancel-btn'
              />
            </Row>
          </Col>
        </Row>
      </Form>
    </Flex>
  );
};

export default UpdateDeliveryAddress;
